const createProgram = ({
  name,
  type,
  sourceUrl,
  mapListingUrl,
  overview,
  credential,
  format,
  length,
  tuition,
  admissions,
  focus,
  highlights,
}) => {
  const isDallasCollege = name.startsWith('Dallas College ');
  const programLength = isDallasCollege ? '4 months - 2 years' : length;
  const programTuition = isDallasCollege ? '$1,089 - $8,640' : tuition;
  const programCredential = isDallasCollege
    ? '10 Certificates or 1 Associate Degree'
    : credential;

  return {
    name,
    type,
    credential: programCredential,
    format,
    length: programLength,
    tuition: programTuition,
    admissions,
    summary: overview,
    badge: type,
    websiteUrl: sourceUrl,
    sourceUrl,
    specs: [
      ['Program type', type],
      ['Credential', programCredential],
      ['Class format', format],
      ['Program length', programLength],
      ['Tuition and fees', programTuition],
      ['Admissions', admissions],
    ],
    paragraphs: [
      focus,
      'Review the official program page for current course availability, enrollment dates, program requirements, campus details, and any changes to published costs or credentials.',
    ],
    highlights,
    address: `${name}, Dallas-Fort Worth, TX`,
    mapListingUrl,
    mapEmbedUrl: `https://www.google.com/maps?q=${encodeURIComponent(name)}&output=embed`,
  };
};

export const programs = [
  createProgram({
    name: 'Dallas College Brookhaven Campus',
    type: 'Community college HVAC training',
    sourceUrl: 'https://www.dallascollege.edu/study/hvac/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Dallas+College+Brookhaven+Campus&query_place_id=ChIJb8_qFCQnTIYRDPKPtJBh5AI',
    overview: 'Brookhaven Campus provides an engaging, hands-on environment for students eager to enter the heating, ventilation, and air conditioning industry. The program emphasizes practical climate-control instruction and electrical fundamentals in well-equipped labs.',
    credential: 'HVAC career certificate and technical coursework; verify current credential options',
    format: 'Campus-based technical instruction with hands-on laboratory practice',
    length: 'Varies by certificate and course sequence',
    tuition: 'Published tuition depends on credit hours, residency, course load, and applicable fees',
    admissions: 'Review Dallas College admissions and program requirements',
    focus: 'Brookhaven is designed for students who want to build core HVAC competencies, prepare for industry certification testing, and step into the North Texas workforce. Ask about current lab access, class schedules, electrical coursework, certification preparation, and available career resources.',
    highlights: [
      'Hands-on climate-control and electrical fundamentals',
      'Well-equipped campus laboratory environment',
      'Ask about certification preparation and North Texas career support',
    ],
  }),
  createProgram({
    name: 'Dallas College Cedar Valley Campus',
    type: 'Community college HVAC training',
    sourceUrl: 'https://www.dallascollege.edu/study/hvac/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Dallas+College+Cedar+Valley+Campus&query_place_id=ChIJ4YhMQ-KVToYRp5L2c6XsnjY',
    overview: 'Cedar Valley Campus combines a comprehensive HVAC curriculum with industry-focused relationships, including connections with organizations such as the Air Conditioning Contractors of America. Students blend classroom theory with hands-on practice in modern residential and commercial applications.',
    credential: 'HVAC career certificate and technical coursework; verify current credential options',
    format: 'Campus-based technical instruction with residential and commercial applications',
    length: 'Varies by certificate and course sequence',
    tuition: 'Published tuition depends on credit hours, residency, course load, and applicable fees',
    admissions: 'Review Dallas College admissions and program requirements',
    focus: 'Cedar Valley offers options ranging from a short occupational skills award to a longer associate degree pathway. Confirm which courses are currently offered, how industry partnerships support learning, and which pathway best matches your career plans.',
    highlights: [
      'Residential and commercial HVAC applications',
      'Industry-connected curriculum and contractor relationships',
      'Pathways from occupational awards to associate degrees',
    ],
  }),
  createProgram({
    name: 'Dallas College Eastfield Campus',
    type: 'Community college HVAC training',
    sourceUrl: 'https://www.dallascollege.edu/study/hvac/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Dallas+College+Eastfield+Campus&query_place_id=ChIJ-whta_mjToYREI4ThnVbjmc',
    overview: 'Eastfield Campus delivers targeted HVAC training for students preparing to work in one of Texas’s essential, high-demand industries. Students develop real-world skills through local contractor connections and immersive campus laboratory work.',
    credential: 'HVAC career certificate and technical coursework; verify current credential options',
    format: 'Campus-based technical instruction with active laboratory practice',
    length: 'Varies by certificate and course sequence',
    tuition: 'Published tuition depends on credit hours, residency, course load, and applicable fees',
    admissions: 'Review Dallas College admissions and program requirements',
    focus: 'Eastfield students practice with refrigeration systems, electrical controls, and air systems while choosing between flexible certificate and degree tracks. Ask about course pacing, contractor relationships, laboratory hours, installation practice, and service troubleshooting.',
    highlights: [
      'Refrigeration, electrical controls, and air-system practice',
      'Flexible certificate and degree pathways',
      'Local contractor connections and hands-on campus labs',
    ],
  }),
  createProgram({
    name: 'Dallas College El Centro Campus',
    type: 'Community college HVAC training',
    sourceUrl: 'https://www.dallascollege.edu/study/hvac/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Dallas+College+El+Centro+Campus&query_place_id=ChIJg5V_sBeZToYRhqUak_RlXOY',
    overview: 'Located in downtown Dallas, El Centro Campus offers convenient, career-focused HVAC training for students pursuing opportunities in the urban job market. The program pairs flexible scheduling with rigorous instruction in air conditioning and refrigeration.',
    credential: 'HVAC career certificate and technical coursework; verify current credential options',
    format: 'Career-focused campus instruction with flexible scheduling options',
    length: 'Varies by certificate and course sequence',
    tuition: 'Published tuition depends on credit hours, residency, course load, and applicable fees',
    admissions: 'Review Dallas College admissions and program requirements',
    focus: 'El Centro helps students develop problem-solving skills for commercial maintenance and independent technical work. Confirm current class times, downtown campus offerings, refrigeration and air-conditioning lab access, and the career resources available to students.',
    highlights: [
      'Central downtown Dallas location',
      'Flexible scheduling for career-focused students',
      'Air-conditioning, refrigeration, and commercial maintenance preparation',
    ],
  }),
  createProgram({
    name: 'Dallas College Mountain View Campus',
    type: 'Community college HVAC training',
    sourceUrl: 'https://www.dallascollege.edu/study/hvac/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Dallas+College+Mountain+View+Campus&query_place_id=ChIJeQU_LL2aToYR5LSVeSr7Pk0',
    overview: 'Mountain View Campus equips future technicians with localized, hands-on skills for residential and light commercial heating and cooling. Students train with industry-standard equipment while learning troubleshooting, wiring, and airflow mechanics.',
    credential: 'HVAC career certificate and technical coursework; verify current credential options',
    format: 'Campus-based technical instruction with industry-standard equipment',
    length: 'Varies by certificate and course sequence',
    tuition: 'Published tuition depends on credit hours, residency, course load, and applicable fees',
    admissions: 'Review Dallas College admissions and program requirements',
    focus: 'Experienced instructors guide students through practical system work in a supportive learning environment. Ask about targeted certificates, current equipment, wiring and airflow labs, troubleshooting exercises, and how the program supports local employment.',
    highlights: [
      'Residential and light commercial HVAC focus',
      'Practice with industry-standard equipment',
      'Targeted certificates designed to strengthen job readiness',
    ],
  }),
  createProgram({
    name: 'Dallas College North Lake Campus',
    type: 'Community college HVAC training',
    sourceUrl: 'https://www.dallascollege.edu/study/hvac/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Dallas+College+North+Lake+Campus&query_place_id=ChIJ4x2g3WKCToYRPy6VvqlDgyo',
    overview: 'North Lake Campus offers robust air conditioning and refrigeration technology pathways that combine foundational science with extensive practical laboratory work. Students can study systems ranging from residential units to complex commercial installations.',
    credential: 'HVAC career certificate and technical coursework; verify current credential options',
    format: 'Technical instruction with extensive air conditioning and refrigeration labs',
    length: 'Varies by certificate and course sequence',
    tuition: 'Published tuition depends on credit hours, residency, course load, and applicable fees',
    admissions: 'Review Dallas College admissions and program requirements',
    focus: 'North Lake provides flexible pacing through short-term workforce certificates and comprehensive associate degree options. Confirm the current lab sequence, commercial equipment coverage, pacing options, professional skills training, and DFW career support.',
    highlights: [
      'Air conditioning and refrigeration technology pathways',
      'Short-term certificate and associate degree options',
      'Residential and complex commercial system preparation',
    ],
  }),
  createProgram({
    name: 'Dallas College Richland Campus',
    type: 'Community college HVAC training',
    sourceUrl: 'https://www.dallascollege.edu/study/hvac/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Dallas+College+Richland+Campus&query_place_id=ChIJcYE7apUfTIYRUohIJ0plNVU',
    overview: 'Richland Campus provides a dynamic educational setting for students pursuing high-demand HVAC careers. Structured, hands-on coursework addresses contemporary heating, ventilation, and air conditioning technologies and prepares students to diagnose diverse equipment setups.',
    credential: 'HVAC career certificate and technical coursework; verify current credential options',
    format: 'Structured campus instruction with hands-on technical coursework',
    length: 'Varies by certificate and course sequence',
    tuition: 'Published tuition depends on credit hours, residency, course load, and applicable fees',
    admissions: 'Review Dallas College admissions and program requirements',
    focus: 'Richland emphasizes practical diagnosis and repair skills across modern HVAC equipment. Ask about current technology coverage, lab assignments, certification preparation, instructor support, and the steps needed to establish a long-term career in the trades.',
    highlights: [
      'Structured training for high-demand HVAC careers',
      'Hands-on diagnosis and repair coursework',
      'Preparation for contemporary equipment and industry certifications',
    ],
  }),
  createProgram({
    name: 'Lincoln College of Technology',
    type: 'Private technical career school',
    sourceUrl: 'https://www.lincolntech.edu/campus/grand-prairie-tx/programs/hvac',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Lincoln+College+of+Technology&query_place_id=ChIJh6wFbdaHToYR5YonU3VIA9E',
    overview: 'Lincoln Tech in Grand Prairie provides targeted, hands-on HVAC training for residential and commercial heating and cooling careers. Students use professional-grade equipment in simulated work environments while developing electrical, climate-control, and troubleshooting skills.',
    credential: 'HVAC training program; verify the current diploma or credential awarded',
    format: 'Hands-on technical training with classroom and simulated work environments',
    length: 'Verify the current Grand Prairie program schedule',
    tuition: 'Request a current tuition, fees, tools, and financing estimate from the campus',
    admissions: 'Contact the Grand Prairie campus for current admissions requirements',
    focus: 'The program is built around practical training, certification preparation, and job readiness in a condensed timeframe. Ask about professional equipment, current program length, career services, employer connections, tool costs, and available financing.',
    highlights: [
      'Grand Prairie residential and commercial HVAC focus',
      'Professional-grade equipment and simulated work environments',
      'Career services and industry connections',
    ],
  }),
  createProgram({
    name: 'Universal Technical Institute',
    type: 'Private technical career school',
    sourceUrl: 'https://www.uti.edu/programs/hvacr',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Universal+Technical+Institute&query_place_id=ChIJsbjprtArTIYRpZ7WwzRTLwo',
    overview: 'UTI’s HVACR program is designed to take beginners toward industry readiness in less than a year. Classroom instruction and intensive laboratory work cover residential and commercial climate-control systems, refrigeration units, and emerging green technologies.',
    credential: 'HVACR technician training; verify the current credential and campus availability',
    format: 'Classroom instruction combined with intensive HVACR laboratory work',
    length: 'Verify the current program length and start dates',
    tuition: 'Request a current tuition and fee estimate directly from UTI',
    admissions: 'Review current UTI admissions requirements and campus options',
    focus: 'Students learn to install, maintain, and repair HVACR equipment while building practical confidence. Compare the current lab schedule, commercial and residential equipment, refrigeration coverage, green technology instruction, employer relationships, and career placement assistance.',
    highlights: [
      'HVACR training designed for completion in less than a year',
      'Residential, commercial, refrigeration, and green technology topics',
      'Employer relationships and career placement assistance',
    ],
  }),
  createProgram({
    name: 'UEI - Dallas',
    type: 'Private career training school',
    sourceUrl: 'https://www.uei.edu/trade-school/texas/dallas/heating-ventilation-and-air-conditioning-hvac-in-dallas/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=UEI+-+Dallas&query_place_id=ChIJX8ldor6bToYRMbLqpYDbE',
    overview: 'UEI’s Dallas HVAC program offers a fast-track route into a hands-on career without requiring a traditional degree. Designed with working students in mind, the program combines flexible scheduling with practical instruction in electrical theory, heating systems, and air-conditioning repair.',
    credential: 'HVAC technician training program; verify the current diploma or credential',
    format: 'Career-focused classroom and hands-on training with flexible scheduling',
    length: 'Verify the current program duration and class schedule',
    tuition: 'Request current tuition, fees, tools, and financial-aid information',
    admissions: 'Review current UEI Dallas admissions requirements',
    focus: 'UEI combines supportive instruction with career services aimed at entry-level employment. Ask about the hands-on lab experience, current schedule options, electrical and heating coursework, AC repair practice, credentials, graduation timeline, and employer support.',
    highlights: [
      'Fast-track Dallas HVAC career pathway',
      'Flexible scheduling for working students',
      'Practical instruction and dedicated career services',
    ],
  }),
  createProgram({
    name: 'Aviation Institute of Maintenance',
    type: 'Private technical training institute',
    sourceUrl: 'https://aviationmaintenance.edu/programs/trade-programs/hvac/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=Aviation+Institute+of+Maintenance&query_place_id=ChIJ7cCtHiKDToYRuDxb7mjMXSw',
    overview: 'AIM’s HVAC training delivers focused, career-oriented education for students entering the mechanical trades. Instructor-led training covers essential heating, ventilation, and air conditioning systems while developing the tactile skills needed for diagnostic and installation work.',
    credential: 'HVAC training program; verify the current credential and campus offering',
    format: 'Career-focused technical instruction with instructor-led hands-on practice',
    length: 'Verify the current program duration and schedule',
    tuition: 'Request current tuition, fees, tools, and financing information',
    admissions: 'Contact the school for current admissions requirements',
    focus: 'AIM emphasizes practical system knowledge, industry standards, and employment preparation. Confirm the local campus and current program availability, then ask about equipment practice, troubleshooting, installation skills, career coaching, and interview preparation.',
    highlights: [
      'Focused HVAC education for mechanical trade careers',
      'Instructor-led diagnostic and installation practice',
      'Employment preparation from enrollment through interviews',
    ],
  }),
  createProgram({
    name: 'South Texas Vocational Technical Institute of Arlington, TX',
    type: 'Private vocational technical school',
    sourceUrl: 'https://hvaccareernow.com/schools/south-texas-vocational-technical-institute/arlington-tx/',
    mapListingUrl: 'https://www.google.com/maps/search/?api=1&query=South+Texas+Vocational+Technical+Institute+of+Arlington,+TX&query_place_id=ChIJj54fE9x4ToYRzc4WyOIS4i4',
    overview: 'South Texas Vocational Technical Institute in Arlington offers practical, career-focused HVAC training built around real-world technical competency. Students work with modern heating, ventilation, and air conditioning equipment while developing job-ready muscle memory and problem-solving skills.',
    credential: 'HVAC technician training; verify the current diploma or credential',
    format: 'Vocational classroom and hands-on technical training',
    length: 'Verify the current Arlington program duration',
    tuition: 'Request a current tuition and fee estimate from the Arlington campus',
    admissions: 'Review current admissions requirements with the school',
    focus: 'The Arlington program is designed for students who want a direct entry into the skilled trades. Ask about current equipment, laboratory hours, heating and cooling systems, instructor support, career services, class schedules, credential details, and total costs.',
    highlights: [
      'Arlington-area practical HVAC training',
      'Modern equipment and hands-on technical practice',
      'Career-focused support for entering the skilled trades',
    ],
  }),
];

export const resources = [
  {
    title: 'Air-Conditioning, Heating, and Refrigeration Institute',
    description: 'Explore industry standards, equipment efficiency information, and HVAC career resources.',
    url: 'https://www.ahrinet.org/',
  },
  {
    title: 'EPA Section 608 Technician Certification',
    description: 'Review federal refrigerant handling requirements and approved certification programs.',
    url: 'https://www.epa.gov/section608',
  },
  {
    title: 'Texas Department of Licensing and Regulation',
    description: 'Find current Texas air conditioning and refrigeration licensing requirements.',
    url: 'https://www.tdlr.texas.gov/acr/acr.htm',
  },
  {
    title: 'U.S. Bureau of Labor Statistics',
    description: 'Research HVAC mechanic and installer duties, pay, education, and employment outlook.',
    url: 'https://www.bls.gov/ooh/installation-maintenance-and-repair/heating-air-conditioning-and-refrigeration-mechanics-and-installers.htm',
  },
];